import { forwardRef, useEffect, useImperativeHandle, useMemo, useRef } from 'react';
import { render } from '@markbird/markdown';
import { renderMermaid } from '../lib/mermaid';
import { renderKatex } from '../lib/katex';
import { resolveImagesInDom } from '../lib/images';
import { resolveWikiLinksInDom } from '../lib/wikilinks';
import { fillEmbeds, placeEmbeds } from '../lib/embeds';
import { renderQueriesInDom } from '../lib/query';
import { setTaskChecked } from '../lib/tasks';
import { onLibraryWrite } from '../lib/library';
import { resolveTarget } from '../lib/linkIndex';
import { useDocControllerContext } from '../hooks/DocControllerContext';
import { useStore } from '../store/useStore';
import { useT } from '../i18n/useT';

export interface PreviewHandle {
  /** Fractional source line currently at the top of the preview viewport. */
  getTopLine: () => number;
  /** Scroll so `line` (may be fractional) is at the top of the viewport. */
  scrollToLine: (line: number) => void;
  getScrollTop: () => number;
  setScrollTop: (top: number) => void;
}

interface PreviewProps {
  content: string;
  onScroll?: () => void;
  className?: string;
}

interface Anchor {
  line: number;
  top: number;
}

export const Preview = forwardRef<PreviewHandle, PreviewProps>(function Preview(
  { content, onScroll, className },
  ref,
) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const bodyRef = useRef<HTMLDivElement>(null);
  const theme = useStore((s) => s.theme);
  const activeDocId = useStore((s) => s.activeDoc.id);
  const controller = useDocControllerContext();
  const t = useT();

  const embedLabels = useMemo(
    () => ({
      missing: (target: string) => t('embed.missing', { target }),
      missingSection: (section: string) => t('embed.missingSection', { section }),
      truncated: (target: string) => t('embed.truncated', { target }),
    }),
    [t],
  );

  const html = useMemo(() => render(content), [content]);

  // Collect [sourceLine -> offsetTop] anchors for sync scrolling.
  const anchors = (): Anchor[] => {
    const body = bodyRef.current;
    if (!body) return [];
    const els = body.querySelectorAll<HTMLElement>('[data-source-line]');
    const out: Anchor[] = [];
    for (const el of els) {
      const line = Number(el.dataset.sourceLine);
      if (Number.isFinite(line)) out.push({ line, top: el.offsetTop });
    }
    return out;
  };

  useImperativeHandle(ref, (): PreviewHandle => ({
    getTopLine: () => {
      const scroller = scrollRef.current;
      const list = anchors();
      if (!scroller || list.length === 0) return 1;
      const y = scroller.scrollTop;
      // Find the last anchor at or above the current scroll position.
      let i = 0;
      while (i < list.length - 1 && list[i + 1]!.top <= y) i++;
      const a = list[i]!;
      const b = list[i + 1];
      if (!b || b.top === a.top) return a.line;
      const frac = (y - a.top) / (b.top - a.top);
      return a.line + frac * (b.line - a.line);
    },
    scrollToLine: (line) => {
      const scroller = scrollRef.current;
      const list = anchors();
      if (!scroller) return;
      if (list.length === 0) return;
      // Find the bracketing anchors for `line` and interpolate pixel offset.
      let i = 0;
      while (i < list.length - 1 && list[i + 1]!.line <= line) i++;
      const a = list[i]!;
      const b = list[i + 1];
      let target: number;
      if (!b || b.line === a.line) {
        target = a.top;
      } else {
        const frac = (line - a.line) / (b.line - a.line);
        target = a.top + frac * (b.top - a.top);
      }
      scroller.scrollTop = Math.max(0, target);
    },
    getScrollTop: () => scrollRef.current?.scrollTop ?? 0,
    setScrollTop: (top) => {
      if (scrollRef.current) scrollRef.current.scrollTop = top;
    },
  }));

  // Inject sanitized HTML imperatively (safe: `render()` sanitizes via
  // rehype-sanitize; no scripts survive). Re-injecting on theme change restores
  // the mermaid source so diagrams re-render with the new theme colors.
  useEffect(() => {
    const body = bodyRef.current;
    if (!body) return;
    body.innerHTML = html;

    // Embeds are turned into placeholder elements first and filled afterwards.
    // The wikilink pass must not see `![[X]]` as text, or it would link the
    // `[[X]]` inside and leave the `!` stranded.
    const embeds = placeEmbeds(body);

    const decorate = (root: HTMLElement) => {
      void renderMermaid(root);
      void renderKatex(root);
      void renderQueriesInDom(root, activeDocId);
      resolveWikiLinksInDom(root);
      void resolveImagesInDom(root).then((r) => revokers.push(r));
    };

    // Task boxes in *this* document can be ticked; the ones inside an embedded
    // note belong to another document and are left alone rather than writing
    // into the wrong file.
    for (const item of body.querySelectorAll<HTMLElement>('li.task-list-item[data-source-line]')) {
      if (item.closest('.mb-embed')) continue;
      const box = item.querySelector<HTMLInputElement>('input[type="checkbox"]');
      const line = Number(item.dataset.sourceLine);
      if (!box || !Number.isInteger(line)) continue;
      box.disabled = false;
      box.addEventListener('change', () => {
        const { activeDoc, setContent } = useStore.getState();
        const next = setTaskChecked(activeDoc.content, line, box.checked);
        // Refused only when the line is no longer that task, which means the
        // preview is about to be rebuilt from newer content anyway.
        if (next !== null) setContent(next);
      });
    }

    const revokers: (() => void)[] = [];
    decorate(body);
    // Embedded content goes through the same passes, so a transcluded diagram,
    // formula or image behaves exactly as it does in its own document.
    void fillEmbeds(embeds, { labels: embedLabels, onRendered: decorate });

    return () => revokers.forEach((r) => r());
  }, [html, theme, embedLabels, activeDocId]);

  // A query block asks about the *library*, so it has to follow a change in any
  // document, not only in this one — which re-rendering the preview would miss.
  useEffect(() => {
    const body = bodyRef.current;
    if (!body) return;
    let timer: number | null = null;
    const unsubscribe = onLibraryWrite(() => {
      if (timer !== null) window.clearTimeout(timer);
      timer = window.setTimeout(() => void renderQueriesInDom(body, activeDocId), 400);
    });
    return () => {
      unsubscribe();
      if (timer !== null) window.clearTimeout(timer);
    };
  }, [activeDocId]);

  // Navigate on wiki-link clicks: open the matching document, or create it.
  useEffect(() => {
    const body = bodyRef.current;
    if (!body) return;
    const onClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement | null;
      const link = target?.closest('.mb-wikilink') as HTMLElement | null;
      if (!link) return;
      e.preventDefault();
      const title = link.dataset.title || link.textContent || '';
      void (async () => {
        const id = await resolveTarget(title);
        if (id) await controller.open(id);
        else await controller.createFrom(`# ${title}\n\n`);
      })();
    };
    body.addEventListener('click', onClick);
    return () => body.removeEventListener('click', onClick);
  }, [controller]);

  return (
    // tabIndex so the preview can be scrolled from the keyboard — it holds no
    // focusable content of its own, so without it arrow keys could never reach it.
    <div
      ref={scrollRef}
      className={`mb-preview-scroll ${className ?? ''}`}
      onScroll={onScroll}
      tabIndex={0}
      role="region"
      aria-label={t('preview.aria')}
    >
      <div ref={bodyRef} className="markbird-preview markdown-preview" />
    </div>
  );
});
